import type { Hanger, HangerHistoryItem } from "@greeter/core";
import type { IVenueApi } from "@greeter/venue-api";
import { createFetchMachine, FetchActor } from "@greeter/xstate-util";
import {
  and,
  assign,
  fromPromise,
  not,
  raise,
  sendTo,
  setup,
  stateIn,
} from "xstate";

export type WardrobeApi = Pick<
  IVenueApi,
  | "fetchHangers"
  | "fetchHangerHistoryItems"
  | "checkoutHanger"
  | "searchHangers"
>;

function focusedId(context: Context): string {
  if (!context.focusedId) throw new Error("A hanger must be selected");
  return context.focusedId;
}

type Context = {
  api: WardrobeApi;
  fetchHangersRef: FetchActor<(api: WardrobeApi) => Promise<Hanger[]>>;
  hangers: Hanger[];
  history: HangerHistoryItem[];
  focusedId?: string;
  focusedHanger?: Hanger;
  search: string;
  searchResults: Hanger[];
};

export const WardrobeOverviewMachine = setup({
  types: {} as {
    context: Context;
    input: { api: WardrobeApi };
    events:
      | { type: "refresh" }
      | { type: "search"; query: string }
      | { type: "refreshSearch" }
      | {
          type: "xstate.snapshot.hangers";
          snapshot: ReturnType<Context["fetchHangersRef"]["getSnapshot"]>;
        }
      | { type: "set.hangers"; data: Hanger[] }
      | { type: "focus"; id?: string }
      | { type: "retryHistory" }
      | { type: "refreshHistory" }
      | { type: "checkout" }
      | { type: "checkedOut" };
  },
  guards: {
    notSaving: not(stateIn({ checkout: "saving" })),
    canCheckout: and([
      ({ context }) =>
        !context.fetchHangersRef.getSnapshot().matches("fetching"),
      ({ context }) => context.focusedHanger?.state === "in-use",
    ]),
  },
  actors: {
    searchHangers: fromPromise(
      async ({
        input,
        signal,
      }: {
        input: { api: WardrobeApi; query: string };
        signal: AbortSignal;
      }) => {
        const abortController = new AbortController();
        const abort = () => abortController.abort();
        signal.addEventListener("abort", abort, { once: true });
        try {
          return await input.api.searchHangers(
            { name: input.query },
            { abortController },
          );
        } finally {
          signal.removeEventListener("abort", abort);
        }
      },
    ),
    fetchHangers: createFetchMachine((api: WardrobeApi) => api.fetchHangers()),
    fetchHistory: fromPromise(
      ({ input }: { input: { api: WardrobeApi; id: string } }) =>
        input.api.fetchHangerHistoryItems({ hangerId: input.id }),
    ),
    checkout: fromPromise(
      ({ input }: { input: { api: WardrobeApi; id: string } }) =>
        input.api.checkoutHanger({ hangerId: input.id }),
    ),
  },
}).createMachine({
  context: ({ input, spawn }) => {
    const fetchHangersRef = spawn("fetchHangers", {
      id: "hangers",
      syncSnapshot: true,
    });
    fetchHangersRef.send({ type: "fetch", data: [input.api] });
    return {
      api: input.api,
      fetchHangersRef,
      hangers: [],
      history: [],
      search: "",
      searchResults: [],
    };
  },
  on: {
    // Snapshot synchronization survives React StrictMode's actor stop/restart cycle.
    "xstate.snapshot.hangers": {
      guard: ({ event }) =>
        event.snapshot.matches("idle") &&
        event.snapshot.context.data !== undefined,
      actions: raise(({ event }) => ({
        type: "set.hangers",
        data: event.snapshot.context.data!,
      })),
    },
    "set.hangers": {
      actions: assign({
        hangers: ({ event }) => event.data,
        focusedHanger: ({ context, event }) =>
          event.data.find((h) => h.id === context.focusedId) ??
          context.focusedHanger,
      }),
    },
    refresh: {
      guard: "notSaving",
      actions: [
        sendTo(
          ({ context }) => context.fetchHangersRef,
          ({ context }) => ({
            type: "fetch" as const,
            data: [context.api] as [WardrobeApi],
          }),
        ),
        raise({ type: "refreshHistory" }),
        raise({ type: "refreshSearch" }),
      ],
    },
  },
  type: "parallel",
  states: {
    search: {
      initial: "idle",
      on: {
        search: {
          target: ".deciding",
          actions: assign({
            search: ({ event }) => event.query,
            searchResults: [],
          }),
        },
        refreshSearch: {
          target: ".deciding",
          actions: assign({ searchResults: [] }),
        },
      },
      states: {
        idle: {},
        deciding: {
          always: [
            {
              guard: ({ context }) => context.search.trim().length >= 2,
              target: "debouncing",
            },
            { target: "idle" },
          ],
        },
        debouncing: { after: { 300: "loading" } },
        loading: {
          invoke: {
            src: "searchHangers",
            input: ({ context }) => ({
              api: context.api,
              query: context.search.trim(),
            }),
            onDone: {
              target: "ready",
              actions: assign({
                searchResults: ({ event }) => event.output,
                focusedHanger: ({ context, event }) =>
                  event.output.find((h) => h.id === context.focusedId) ??
                  context.focusedHanger,
              }),
            },
            onError: "failed",
          },
        },
        ready: {},
        failed: {},
      },
    },
    detail: {
      initial: "closed",
      on: {
        focus: [
          { guard: stateIn({ checkout: "saving" }) },
          {
            guard: ({ event, context }) =>
              !!event.id && event.id !== context.focusedId,
            target: ".loading",
            actions: assign({
              focusedId: ({ event }) => event.id,
              focusedHanger: ({ context, event }) =>
                context.searchResults.find((h) => h.id === event.id) ??
                context.hangers.find((h) => h.id === event.id),
              history: [],
            }),
          },
          {
            target: ".closed",
            actions: assign({
              focusedId: undefined,
              focusedHanger: undefined,
              history: [],
            }),
          },
        ],
      },
      states: {
        closed: {},
        loading: {
          invoke: {
            src: "fetchHistory",
            input: ({ context }) => ({
              api: context.api,
              id: focusedId(context),
            }),
            onDone: {
              target: "ready",
              actions: assign({ history: ({ event }) => event.output }),
            },
            onError: "failed",
          },
          on: { checkedOut: { target: "loading", reenter: true } },
        },
        ready: {
          on: {
            checkedOut: "loading",
            refreshHistory: { target: "loading", guard: "notSaving" },
          },
        },
        failed: {
          on: {
            refreshHistory: { target: "loading", guard: "notSaving" },
            retryHistory: "loading",
            checkedOut: "loading",
          },
        },
      },
    },
    checkout: {
      initial: "idle",
      states: {
        idle: {
          on: {
            checkout: {
              guard: "canCheckout",
              target: "saving",
            },
          },
        },
        saving: {
          invoke: {
            src: "checkout",
            input: ({ context }) => ({
              api: context.api,
              id: focusedId(context),
            }),
            onDone: {
              target: "idle",
              actions: [
                assign({
                  focusedHanger: ({ context }) =>
                    context.focusedHanger
                      ? { ...context.focusedHanger, state: "ready" as const }
                      : undefined,
                  hangers: ({ context }) =>
                    context.hangers.map((h) =>
                      h.id === context.focusedId
                        ? { ...h, state: "ready" as const }
                        : h,
                    ),
                }),
                sendTo(
                  ({ context }) => context.fetchHangersRef,
                  ({ context }) => ({
                    type: "fetch" as const,
                    data: [context.api] as [WardrobeApi],
                  }),
                ),
                raise({ type: "checkedOut" }),
                raise({ type: "refreshSearch" }),
              ],
            },
            onError: "failed",
          },
        },
        failed: {
          on: {
            checkout: { target: "saving", guard: "canCheckout" },
            focus: "idle",
          },
        },
      },
    },
  },
});
